import React, { useState } from 'react';
import { iaApi, workoutApi } from '../services/api';

const AiPlanPage = () => {
  const [instructions, setInstructions] = useState('');
  const [loading, setLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [replaceExisting, setReplaceExisting] = useState(false);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    try {
      const res = await iaApi.suggestWorkout({ instructions });
      setSuggestion(res.data.suggestion);
      setMessage('Plano gerado. Revise e confirme para criar o treino.');
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.message ||
        'Não foi possível gerar o plano. Verifique as chaves de IA no servidor ou no perfil.';
      setError(msg);
      setSuggestion(null);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!suggestion) return;
    setError('');
    setApplyLoading(true);
    try {
      await workoutApi.applySuggestion({ suggestion, replaceExisting });
      setMessage('Treino criado com sucesso na sua conta.');
      setSuggestion(null);
      setInstructions('');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro ao criar treino');
    } finally {
      setApplyLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="font-display text-3xl tracking-wider text-iron-accent">Plano com IA</h1>
        <p className="mt-2 text-sm text-iron-muted">
          Usa o teu estado físico e interesses do perfil + instruções opcionais. Novos exercícios
          passam a existir no catálogo global da app.
        </p>
      </div>

      <form onSubmit={handleGenerate} className="card space-y-4">
        <div>
          <label className="section-label mb-2 block">Instruções opcionais</label>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            className="input min-h-[100px] resize-y font-sans"
            placeholder="Ex.: foco em ombros, 45 min, sem agachamento por dor no joelho..."
          />
        </div>
        <button type="submit" disabled={loading} className="btn-primary w-full py-3 rounded-xl">
          {loading ? 'A gerar…' : 'Gerar plano'}
        </button>
      </form>

      {error && (
        <div className="rounded-xl border border-iron-danger/50 bg-iron-danger/10 px-4 py-3 text-sm text-iron-danger">
          {error}
        </div>
      )}

      {message && !error && (
        <div className="rounded-xl border border-iron-success/40 bg-iron-success/10 px-4 py-3 text-sm text-iron-success">
          {message}
        </div>
      )}

      {suggestion && (
        <div className="space-y-4">
          <div className="section-label flex justify-between items-center">
            <span>Pré-visualização</span>
          </div>
          <div className="card border-iron-accent/40">
            <h2 className="font-display text-2xl text-iron-text tracking-wide">{suggestion.name}</h2>
            {suggestion.description && (
              <p className="mt-2 text-sm text-iron-muted whitespace-pre-wrap">{suggestion.description}</p>
            )}
            <label className="flex items-center gap-2 font-mono text-xs text-iron-muted cursor-pointer">
              <input
                type="checkbox"
                checked={replaceExisting}
                onChange={(e) => setReplaceExisting(e.target.checked)}
                className="rounded border-iron-border"
              />
              Substituir treino com o mesmo nome (se existir)
            </label>
            <ul className="mt-4 space-y-3">
              {(suggestion.items || []).map((it, idx) => (
                <li
                  key={`${it.name}-${idx}`}
                  className="rounded-xl border border-iron-border bg-iron-surface/80 px-4 py-3"
                >
                  <div className="flex justify-between gap-2">
                    <span className="font-display text-xl text-iron-accent">{idx + 1}</span>
                    <span className="flex-1 font-medium text-iron-text">{it.name}</span>
                  </div>
                  <div className="mt-2 font-mono text-[10px] text-iron-muted uppercase tracking-wide">
                    {it.muscleGroup || '—'} · {it.sets != null ? `${it.sets} séries` : '—'} · reps{' '}
                    {it.reps ?? '—'} · descanso {it.restSeconds ?? '—'}s
                  </div>
                  {it.notes && <p className="mt-2 text-xs text-iron-muted">{it.notes}</p>}
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={handleApply}
              disabled={applyLoading}
              className="btn-primary mt-6 w-full py-3 rounded-xl"
            >
              {applyLoading ? 'A criar treino…' : 'Criar este treino na app'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiPlanPage;
